import React, { useEffect, useState } from 'react';
import { Form, message, Spin } from 'antd';
import { getCredPayoutAccount, saveCredPayoutAccount } from 'api/credentialing';
import type { ProviderCredentialingPayoutAccountDto } from 'api/credentialing/types';
import {
    PayoutAccountFields,
    PAYOUT_FORM_DEFAULTS,
    buildPayoutDto,
    filledPayoutValues,
} from 'components/CredentialingPayoutForm';
import { fileNameFromKey } from 'utils/file';
import ReviewSection from '../ReviewSection';
import s from '../../s.module.less';

const ACCOUNT_TYPE_LABEL: Record<string, string> = { CHECKING: 'Checking', SAVINGS: 'Savings' };

const maskAccount = (num?: string) => (num ? `•••• ${num.slice(-4)}` : '');

const joinAddr = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(', ');

const item = (label: string, value?: string) => (
    <div className={s.reviewItem} key={label}>
        <div className={s.reviewItemLabel}>{label}</div>
        <div className={value ? s.reviewItemValue : s.reviewItemEmpty}>{value || 'Not provided'}</div>
    </div>
);

// Settings 弹窗的 Payout account tab：credentialing 收款账户（practice 级，group 下仅 owner 可见），
// 只读四行 + 行内编辑，读写走 payout-account 独立接口（不复用 api/paymentSetup）。
export default function PayoutAccountSection() {
    const [form] = Form.useForm<ProviderCredentialingPayoutAccountDto>();
    const [account, setAccount] = useState<ProviderCredentialingPayoutAccountDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res: any = await getCredPayoutAccount();
                if (!cancelled) setAccount(res?.data?.data || null);
            } catch (e) {
                // ignore：无已存账户按空处理
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const openEdit = () => {
        form.resetFields();
        if (account) form.setFieldsValue(filledPayoutValues(account));
        setEditing(true);
    };

    const save = async () => {
        try {
            await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        try {
            // 接口整份覆盖：以已存账户为底叠上表单值，表单没展示的字段不会被清空
            const dto = buildPayoutDto(account, form.getFieldsValue());
            const res: any = await saveCredPayoutAccount(dto);
            if (res?.error) {
                message.error(res.error || 'Failed to save');
                return;
            }
            setAccount(dto);
            setEditing(false);
            message.success('Payout account added. Reimbursements will be deposited to this account.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Spin spinning={loading}>
            <ReviewSection
                sectionKey="payout-account"
                title="Payout account"
                editing={editing}
                saving={saving}
                // 仅在 Settings 弹窗使用：编辑态 Cancel/Save 固定在吸顶标题行
                actionsInHeader
                onEdit={openEdit}
                onCancel={() => setEditing(false)}
                onSave={save}
                viewContent={
                    account?.accountNumber ? (
                        <div className={s.reviewRow}>
                            {item('Bank name', account.bankName)}
                            {item('Account holder name', account.accountHolder)}
                            {item('Account type', account.accountType ? ACCOUNT_TYPE_LABEL[account.accountType] : '')}
                            {item('Account number', maskAccount(account.accountNumber))}
                            {item(
                                'Account holder address',
                                account.holderSameAsPractice
                                    ? 'Same as practice address'
                                    : joinAddr(
                                          account.holderStreet,
                                          account.holderCity,
                                          account.holderState,
                                          account.holderZip,
                                      ),
                            )}
                            {item(
                                'Bank address',
                                joinAddr(account.bankStreet, account.bankCity, account.bankState, account.bankZip),
                            )}
                            {item(
                                'Voided check or bank letter',
                                account.proofFileName || fileNameFromKey(account.proofFileUrl) || '',
                            )}
                        </div>
                    ) : (
                        // 未设置过（如 submitted 页跳过了 payout 设置卡）：原型的 Not set up 空态，Edit 补录
                        <div className={s.reviewRow}>
                            {item('Status', 'Not set up')}
                            {item('Bank', '')}
                            {item('Account holder', '')}
                        </div>
                    )
                }
            >
                {/* 不用 component={false}：ant-form-vertical 等 class 挂在 form 元素上，
                    没有真实元素时 Form.Item 会按 horizontal 样式渲染（label 冒号 + 左右错位） */}
                <Form form={form} layout="vertical" initialValues={PAYOUT_FORM_DEFAULTS}>
                    <p className={s.payoutDesc}>
                        Add the bank account where your insurance reimbursements will be deposited once you&apos;re
                        credentialed. Payers pay this account <b>directly</b>, and your bank details are encrypted and
                        never shared.
                    </p>
                    <PayoutAccountFields />
                </Form>
            </ReviewSection>
        </Spin>
    );
}
